import { useCallback, useMemo } from 'react';

import { OnErrorFn } from '@formatjs/intl/src/types';
import { flatten } from 'utils';
import messagesInEnglish from 'lang/en.json';
import messagesInEnglishGB from 'lang/en-GB.json';
import messagesInEnglishCA from 'lang/en-CA.json';
import messagesInEnglishIE from 'lang/en-IE.json';
import messagesInEnglishAU from 'lang/en-AU.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInFrench from 'lang/fr-FR.json';
import messagesInCanadianFrench from 'lang/fr-CA.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSlovak from 'lang/sk-SK.json';

export const DEFAULT_LOCALE = 'en';

// Locale keys are lowercase-hyphenated; getAvailableLocale normalizes user input
// (lowercases + converts underscores to hyphens) before lookup, so a canonical tag
// from navigator.language (e.g. `fr-FR`) resolves — see VIS-1695. Bare-language
// aliases (de, es, fr, it, nb/no, nl, pt, hu, sk) map a region-less locale onto a catalogue.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.visitorRegistration],
  ['en-gb', messagesInEnglishGB.visitorRegistration],
  ['en-ca', messagesInEnglishCA.visitorRegistration],
  ['en-ie', messagesInEnglishIE.visitorRegistration],
  ['en-au', messagesInEnglishAU.visitorRegistration],
  ['es-es', messagesInSpanish.visitorRegistration],
  ['es', messagesInSpanish.visitorRegistration],
  ['fr-fr', messagesInFrench.visitorRegistration],
  ['fr', messagesInFrench.visitorRegistration],
  ['fr-ca', messagesInCanadianFrench.visitorRegistration],
  ['de-de', messagesInGerman.visitorRegistration],
  ['de', messagesInGerman.visitorRegistration],
  ['nl-nl', messagesInDutch.visitorRegistration],
  ['nl', messagesInDutch.visitorRegistration],
  ['it-it', messagesInItalian.visitorRegistration],
  ['it', messagesInItalian.visitorRegistration],
  ['nb-no', messagesInNorwegian.visitorRegistration],
  ['nb', messagesInNorwegian.visitorRegistration],
  ['no', messagesInNorwegian.visitorRegistration],
  ['pl', messagesInPolish.visitorRegistration],
  ['pt-pt', messagesInPortuguese.visitorRegistration],
  ['pt', messagesInPortuguese.visitorRegistration],
  ['hu-hu', messagesInHungarian.visitorRegistration],
  ['hu', messagesInHungarian.visitorRegistration],
  ['sk-sk', messagesInSlovak.visitorRegistration],
  ['sk', messagesInSlovak.visitorRegistration],
]);

// A structurally invalid tag reaches Intl only via the bare-language fallback: an input like
// `de_` or `de__AT` normalizes to `de-` / `de--at`, which still matches the `de` alias but is not
// a usable BCP-47 tag, so `Intl.NumberFormat(locale)` would throw a RangeError at render time.
// Keep the full normalized tag only when Intl can consume it; otherwise fall back to the bare
// language, which always can.
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}
export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
    };
  }, [userLocale]);
};

export const useErrorHandler = (): OnErrorFn => {
  return useCallback((err) => {
    if (err.code === 'MISSING_TRANSLATION') {
      // eslint-disable-next-line no-console
      console.warn('Missing translation', err.message);
      return;
    }
    throw err;
  }, []);
};
